import { Invoice } from '@dutchinvoice/types';
import { FlattenedKeys } from '../i18n/types';

interface PDFGeneratorOptions {
  margin?: number | [number, number] | [number, number, number, number];
  filename?: string;
  pageBreakSelector?: string;
  imageQuality?: number;
  scale?: number;
  translate?: (key: FlattenedKeys, values?: Record<string, string | number>) => string;
}

const DEFAULT_OPTIONS: PDFGeneratorOptions = {
  margin: [8, 10, 8, 10], // Reduced vertical margins (top/bottom: 8mm, left/right: 10mm)
  pageBreakSelector: '.html2pdf__page-break',
  imageQuality: 0.95,
  scale: 2
};

const A4_DIMENSIONS = {
  width: 210, // mm
  height: 297, // mm
  dpi: 96,
  contentHeight: 281, // Increased to account for reduced margins
};

const createCleanContainer = (): HTMLDivElement => {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  container.style.width = `${A4_DIMENSIONS.width}mm`;
  container.style.background = 'white';
  container.style.zIndex = '-9999';
  return container;
};

const cleanupElement = (element: HTMLElement): void => {
  // Remove all elements marked for PDF exclusion
  element.querySelectorAll('[data-pdf-exclude="true"]').forEach(el => el.remove());

  // Remove contenteditable attribute from editable elements
  element.querySelectorAll('[contenteditable="true"]').forEach(el => {
    if (el instanceof HTMLElement) {
      el.removeAttribute('contenteditable');
    }
  });

  // Remove hover effects and cursor changes but keep the elements
  element.querySelectorAll('*').forEach(el => {
    if (el instanceof HTMLElement) {
      const classes = el.className.split(' ').filter(cls => (
        !cls.includes('hover:') &&
        !cls.includes('focus:') &&
        !cls.includes('cursor-') &&
        !cls.includes('group-hover:')
      )).join(' ');
      el.className = classes;
    }
  });
};

const addPageNumber = (pdf: any, pageNumber: number, totalPages: number, translate?: (key: FlattenedKeys, values?: Record<string, string | number>) => string): void => {
  if (totalPages > 1) {
    const text = translate
      ? translate('invoice.pagination', { page: pageNumber, total: totalPages })
      : `Page ${pageNumber} of ${totalPages}`; // Fallback if no translation function provided
    const fontSize = 8;
    pdf.setFontSize(fontSize);
    const textWidth = pdf.getTextWidth(text);
    pdf.text(text, (A4_DIMENSIONS.width - textWidth) / 2, A4_DIMENSIONS.height - 6);
  }
};

interface PageSection {
  header: HTMLElement;
  senderReceiver: HTMLElement | null;
  tableHeaders: HTMLElement | null;
  colGroup: HTMLElement | null;
  items: HTMLElement[];
  footer: HTMLElement | null;
}

const extractSections = (element: HTMLElement, invoice: Invoice, translate?: (key: FlattenedKeys, values?: Record<string, string | number>) => string): PageSection => {
  const clone = element.cloneNode(true) as HTMLElement;

  // Get the header with invoice number and payment terms
  const header = clone.querySelector('.invoice-header') as HTMLElement;

  // Get the sender/receiver section
  const senderReceiver = clone.querySelector('.invoice-sender-receiver') as HTMLElement;

  // Get table headers and items
  const table = clone.querySelector('table');
  const colGroup = table?.querySelector('colgroup');
  const thead = table?.querySelector('thead');
  const tbody = table?.querySelector('tbody');
  const items = Array.from(tbody?.querySelectorAll('tr') || []);

  // Get the footer (everything after the items, including totals)
  const tfoot = table?.querySelector('tfoot');

  // Also get payment details and notes
  const paymentDetails = clone.querySelector('.invoice-payment-details');
  const notes = clone.querySelector('.invoice-notes');

  // Create a wrapper div for payment details and notes to ensure full width
  const footerExtrasWrapper = document.createElement('div');
  footerExtrasWrapper.style.width = '100%';
  footerExtrasWrapper.style.display = 'block';
  footerExtrasWrapper.style.marginTop = '30px'; // Increased top margin

  // Include payment details if they exist (for IBAN, account info, etc.)
  const hasPaymentLinks = Array.isArray(invoice.paymentLinks) && invoice.paymentLinks.length > 0;

  if (paymentDetails) {
    // Remove buttons and replace with text links (only for online payment methods)
    if (hasPaymentLinks) {
      const buttons = paymentDetails.querySelectorAll('button');
      buttons.forEach(button => {
        const text = button.textContent?.trim();
        if (text) {
          const span = document.createElement('p');
          // Use the most recent payment link URL
          const mostRecentPaymentLink = invoice.paymentLinks?.[0];
          if (mostRecentPaymentLink?.url) {
            span.textContent = `${text}: ${mostRecentPaymentLink.url}`;
            span.className = 'text-sm text-gray-600 mt-2';
            button.parentNode?.replaceChild(span, button);
          }
        }
      });
    }

    // Create a new div for payment details with proper styling
    const paymentWrapper = document.createElement('div');
    paymentWrapper.style.width = '100%';
    paymentWrapper.style.borderTop = '1px solid #e5e7eb';
    paymentWrapper.style.paddingTop = '20px'; // Increased padding
    paymentWrapper.style.marginBottom = '20px';

    // Add a title to payment section using the translate function if available
    const paymentTitle = document.createElement('p');
    paymentTitle.textContent = translate ? translate('invoice.paymentMethods.title') : 'Payment Details';
    paymentTitle.style.fontWeight = 'bold';
    paymentTitle.style.marginBottom = '10px';
    paymentWrapper.appendChild(paymentTitle);

    // Move the payment details content to the wrapper
    while (paymentDetails.firstChild) {
      paymentWrapper.appendChild(paymentDetails.firstChild);
    }

    // Add the wrapper to the footer extras
    footerExtrasWrapper.appendChild(paymentWrapper);
  }

  // Format notes for PDF
  if (notes) {
    // Create a new div for notes with proper styling
    const notesWrapper = document.createElement('div');
    notesWrapper.style.width = '100%';
    notesWrapper.style.borderTop = '1px solid #e5e7eb';
    notesWrapper.style.paddingTop = '10px';

    // Move the notes content to the wrapper (without adding a title)
    while (notes.firstChild) {
      notesWrapper.appendChild(notes.firstChild);
    }

    // Add the wrapper to the footer extras
    footerExtrasWrapper.appendChild(notesWrapper);
  }

  // If we have payment details or notes, append the wrapper to tfoot
  if (tfoot && (paymentDetails || notes)) {
    // Create a full-width row for the footer extras
    const fullWidthRow = document.createElement('tr');
    const fullWidthCell = document.createElement('td');
    fullWidthCell.setAttribute('colspan', '6'); // Ensure it spans all columns
    fullWidthCell.style.width = '100%';
    fullWidthCell.style.padding = '0';

    // Add the wrapper to the cell
    fullWidthCell.appendChild(footerExtrasWrapper);
    fullWidthRow.appendChild(fullWidthCell);
    tfoot.appendChild(fullWidthRow);
  }

  return {
    header: header || document.createElement('div'),
    senderReceiver,
    tableHeaders: thead || null,
    colGroup: colGroup || null,
    items: items as HTMLElement[],
    footer: tfoot as HTMLElement
  };
};

const createPageElement = (
  header: HTMLElement,
  senderReceiver: HTMLElement | null,
  tableHeaders: HTMLElement | null,
  colGroup: HTMLElement | null,
  items: HTMLElement[],
  footer: HTMLElement | null,
  showFooter: boolean,
  isFirstPage: boolean,
  margin: number
): HTMLElement => {
  const page = document.createElement('div');
  page.style.padding = typeof margin === 'number'
    ? `${margin}mm`
    : `${margin[0]}mm ${margin[1]}mm ${margin[2]}mm ${margin[3]}mm`;
  page.style.width = `${A4_DIMENSIONS.width}mm`;
  page.style.maxHeight = `${A4_DIMENSIONS.contentHeight}mm`;
  page.style.background = 'white';
  page.style.position = 'relative';
  page.style.boxSizing = 'border-box';

  // Add header with minimal spacing
  const headerClone = header.cloneNode(true) as HTMLElement;
  headerClone.style.marginBottom = '16px'; // Increased margin for better spacing
  page.appendChild(headerClone);

  // Add sender/receiver section only on the first page
  if (isFirstPage && senderReceiver) {
    const senderReceiverClone = senderReceiver.cloneNode(true) as HTMLElement;
    senderReceiverClone.style.marginBottom = '24px'; // Increased margin for better spacing
    page.appendChild(senderReceiverClone);
  }

  // Create table structure with minimal spacing
  const table = document.createElement('table');
  table.style.width = '100%';
  table.style.marginBottom = '8px';
  table.style.borderCollapse = 'collapse';

  // Add colgroup to maintain column widths
  if (colGroup) {
    const colGroupClone = colGroup.cloneNode(true) as HTMLElement;
    table.appendChild(colGroupClone);
  }

  // Add table headers to each page
  if (tableHeaders) {
    const theadClone = tableHeaders.cloneNode(true) as HTMLElement;
    // Ensure header styles are preserved
    theadClone.querySelectorAll('th').forEach(th => {
      th.style.textAlign = window.getComputedStyle(th).textAlign;
      th.style.width = window.getComputedStyle(th).width;
      th.style.padding = window.getComputedStyle(th).padding;
    });
    table.appendChild(theadClone);
  }

  const tbody = document.createElement('tbody');

  // Add items with minimal row spacing and preserve alignment
  items.forEach(item => {
    const itemClone = item.cloneNode(true) as HTMLElement;
    itemClone.style.marginBottom = '2px';
    // Preserve cell alignments
    itemClone.querySelectorAll('td').forEach(td => {
      td.style.textAlign = window.getComputedStyle(td).textAlign;
      td.style.padding = window.getComputedStyle(td).padding;
    });
    tbody.appendChild(itemClone);
  });

  table.appendChild(tbody);

  // Add footer if this is the last page with minimal spacing
  if (showFooter && footer) {
    const footerClone = footer.cloneNode(true) as HTMLElement;
    footerClone.style.marginTop = '8px';
    // Preserve footer cell alignments
    footerClone.querySelectorAll('td').forEach(td => {
      td.style.textAlign = window.getComputedStyle(td).textAlign;
      td.style.padding = window.getComputedStyle(td).padding;
    });
    table.appendChild(footerClone);
  }

  page.appendChild(table);
  return page;
};

const splitIntoPages = (element: HTMLElement, margin: number, invoice: Invoice, translate?: (key: FlattenedKeys, values?: Record<string, string | number>) => string): HTMLElement[] => {
  const sections = extractSections(element, invoice, translate);
  const pages: HTMLElement[] = [];

  // Adjust items per page based on whether it's the first page (with sender/receiver)
  const firstPageItems = 12; // Fewer items on first page to accommodate sender/receiver
  const subsequentPageItems = 18; // More items on subsequent pages

  let currentIndex = 0;

  // Handle first page
  const firstPageItemsSlice = sections.items.slice(0, firstPageItems);
  pages.push(createPageElement(
    sections.header,
    sections.senderReceiver,
    sections.tableHeaders,
    sections.colGroup,
    firstPageItemsSlice,
    sections.footer,
    firstPageItemsSlice.length === sections.items.length,
    true,
    margin
  ));
  currentIndex += firstPageItems;

  // Handle subsequent pages
  while (currentIndex < sections.items.length) {
    const pageItems = sections.items.slice(
      currentIndex,
      currentIndex + subsequentPageItems
    );
    const isLastPage = currentIndex + subsequentPageItems >= sections.items.length;

    pages.push(createPageElement(
      sections.header,
      null,
      sections.tableHeaders,
      sections.colGroup,
      pageItems,
      sections.footer,
      isLastPage,
      false,
      margin
    ));

    currentIndex += subsequentPageItems;
  }

  // If no items, create at least one page with header and footer
  if (pages.length === 0) {
    pages.push(createPageElement(
      sections.header,
      sections.senderReceiver,
      sections.tableHeaders,
      sections.colGroup,
      [],
      sections.footer,
      true,
      true,
      margin
    ));
  }

  return pages;
};

const calculateMargins = (margins: PDFGeneratorOptions['margin']): [number, number, number, number] => {
  if (typeof margins === 'number') {
    return [margins, margins, margins, margins];
  } else if (Array.isArray(margins) && margins.length === 2) {
    const [vertical, horizontal] = margins;
    return [vertical, horizontal, vertical, horizontal];
  } else if (Array.isArray(margins) && margins.length === 4) {
    return margins;
  }
  return [DEFAULT_OPTIONS.margin as number, DEFAULT_OPTIONS.margin as number, DEFAULT_OPTIONS.margin as number, DEFAULT_OPTIONS.margin as number];
};

export const generateInvoicePDF = async (
  invoiceElement: HTMLElement,
  invoice: Invoice,
  options: PDFGeneratorOptions = {}
): Promise<void> => {
  // Dynamically import the required libraries only when this function is called
  const [html2canvas, { default: jsPDF }] = await Promise.all([
    import('html2canvas').then(module => module.default),
    import('jspdf')
  ]);

  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const { translate } = mergedOptions;
  const margins = calculateMargins(mergedOptions.margin);

  try {
    // Create temporary container
    const container = createCleanContainer();
    document.body.appendChild(container);

    // Clean up the element first
    const cleanElement = invoiceElement.cloneNode(true) as HTMLElement;
    cleanupElement(cleanElement);

    // Split into pages - pass the translate function and invoice
    const pages = splitIntoPages(cleanElement, margins[0], invoice, translate);
    const totalPages = pages.length;

    // Initialize PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
      hotfixes: ['px_scaling']
    });

    // Process each page
    for (let pageNumber = 0; pageNumber < totalPages; pageNumber++) {
      if (pageNumber > 0) {
        pdf.addPage();
      }

      container.innerHTML = '';
      container.appendChild(pages[pageNumber]);

      // Ensure fonts are loaded
      await document.fonts.ready;

      // Create canvas for this page
      const canvas = await html2canvas(container, {
        scale: 2,
        logging: false,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true
      });

      // Calculate dimensions
      const pageWidth = A4_DIMENSIONS.width;
      const imgWidth = pageWidth - (margins[1] + margins[3]);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add content
      pdf.addImage(
        canvas.toDataURL('image/png', 1.0),
        'PNG',
        margins[3],
        margins[0],
        imgWidth,
        imgHeight,
        undefined,
        'FAST'
      );

      // Add page number if multiple pages
      addPageNumber(pdf, pageNumber + 1, totalPages, translate);
    }

    // Save PDF
    const filename = mergedOptions.filename || `invoice_${invoice.invoiceNumber || 'draft'}.pdf`;
    pdf.save(filename);

    // Cleanup
    document.body.removeChild(container);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};

